import { create } from 'zustand';
import type { AppPlan } from '@/webapp/plan/planTypes';

/**
 * What someone has said about the app they want, before it exists.
 *
 * Replaces the shared onboardingStore, which carries a dozen fields the web never uses and
 * pulls the mobile storage layer in with it. This holds exactly what the three /new screens
 * write and the build reads, and nothing else.
 *
 * Deliberately NOT persisted. A half-described app is not worth restoring across sessions,
 * and a stale plan reappearing under a new idea is the bug that made the phone's flow
 * confusing — which is why the idea screen clears the plan on every change.
 */
export interface Draft {
  intent: 'build' | 'browse' | null;
  kind: 'personal' | 'sharable';
  description: string;
  appName: string;
  vibe: string | null;
  plan: AppPlan | null;
  /** Where to resume after a sign-up the build demanded. */
  pendingIntent: 'build' | 'browse' | null;

  setIntent: (intent: 'build' | 'browse') => void;
  setKind: (kind: 'personal' | 'sharable') => void;
  setDescription: (description: string) => void;
  setAppName: (appName: string) => void;
  setVibe: (vibe: string | null) => void;
  setPlan: (plan: AppPlan | null) => void;
  setPendingIntent: (intent: 'build' | 'browse' | null) => void;
  reset: () => void;
}

const EMPTY = {
  intent: null,
  kind: 'personal',
  description: '',
  appName: '',
  vibe: null,
  plan: null,
  pendingIntent: null,
} satisfies Omit<Draft, 'setIntent' | 'setKind' | 'setDescription' | 'setAppName' | 'setVibe' | 'setPlan' | 'setPendingIntent' | 'reset'>;

export const useDraft = create<Draft>((set) => ({
  ...EMPTY,
  setIntent: (intent) => set({ intent }),
  setKind: (kind) => set({ kind }),
  setDescription: (description) => set({ description }),
  setAppName: (appName) => set({ appName }),
  setVibe: (vibe) => set({ vibe }),
  setPlan: (plan) => set({ plan }),
  setPendingIntent: (pendingIntent) => set({ pendingIntent }),
  reset: () => set(EMPTY),
}));
